import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetchJson } from '../../apiClient';
import './AddWineForm.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

const WINE_TYPES = ['Red', 'White', 'Rosé', 'Sparkling', 'Dessert', 'Fortified'];

function AddWineForm({ instance, onSave, onCancel, showMessage }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState('search'); // 'search' | 'new'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedWineId, setSelectedWineId] = useState(null);
  const [producers, setProducers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [saving, setSaving] = useState(false);

  // New wine fields
  const [wineName, setWineName] = useState('');
  const [producer, setProducer] = useState('');
  const [region, setRegion] = useState('');
  const [vintage, setVintage] = useState('');
  const [wineType, setWineType] = useState('Red');
  const [grapeBlend, setGrapeBlend] = useState('');
  const [alcoholPct, setAlcoholPct] = useState('');

  // Cellar entry fields
  const [quantity, setQuantity] = useState(1);
  const [purchasePrice, setPurchasePrice] = useState('');
  const [currency, setCurrency] = useState('NOK');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [storageLocation, setStorageLocation] = useState('');
  const [drinkFrom, setDrinkFrom] = useState('');
  const [drinkBy, setDrinkBy] = useState('');
  const [notes, setNotes] = useState('');

  const loadDropdowns = useCallback(async () => {
    try {
      const [prodResult, regResult] = await Promise.allSettled([
        apiFetchJson(instance, '/api/wine/producers'),
        apiFetchJson(instance, '/api/wine/regions'),
      ]);
      if (prodResult.status === 'fulfilled') setProducers(unwrap(prodResult.value));
      if (regResult.status === 'fulfilled') setRegions(unwrap(regResult.value));
    } catch (e) {
      console.error('Failed to load dropdowns:', e);
    }
  }, [instance]);

  useEffect(() => {
    loadDropdowns();
  }, [loadDropdowns]);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    try {
      const data = await apiFetchJson(instance, `/api/wine/search?query=${encodeURIComponent(searchQuery)}`);
      setSearchResults(unwrap(data));
    } catch (e) {
      console.error('Wine search failed:', e);
    }
  }, [instance, searchQuery]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let wineId = selectedWineId;

      // Create new wine if needed
      if (mode === 'new') {
        const newWine = await apiFetchJson(instance, '/api/wine', {
          method: 'POST',
          body: JSON.stringify({
            name: wineName,
            producer,
            region,
            vintage: vintage ? parseInt(vintage, 10) : null,
            type: wineType,
            grapeBlend,
            alcoholPercentage: alcoholPct ? parseFloat(alcoholPct) : null,
          }),
        });
        wineId = newWine.id;
      }

      if (!wineId) {
        showMessage(t('grapeEscape.addWine.selectWine', 'Please select or create a wine.'), 'error');
        setSaving(false);
        return;
      }

      // Create cellar entry
      await apiFetchJson(instance, '/api/cellar', {
        method: 'POST',
        body: JSON.stringify({
          wineId,
          quantity: parseInt(quantity, 10) || 1,
          purchasePrice: purchasePrice ? parseFloat(purchasePrice) : null,
          currency,
          purchaseDate: purchaseDate || null,
          storageLocation,
          drinkFrom: drinkFrom || null,
          drinkBy: drinkBy || null,
          notes,
        }),
      });
      onSave();
    } catch (e) {
      console.error('Failed to save wine:', e);
      showMessage(t('grapeEscape.addWine.saveError', 'An error occurred while saving.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ge-add-wine">
      <div className="ge-section-header">
        <h3>{t('grapeEscape.addWine.title', 'Add Wine to Cellar')}</h3>
        <button className="ge-btn-secondary" onClick={onCancel}>
          {t('grapeEscape.addWine.cancel', 'Cancel')}
        </button>
      </div>

      {/* Wine Selection Mode */}
      <div className="ge-wine-mode-toggle">
        <button
          className={`ge-toggle-btn ${mode === 'search' ? 'active' : ''}`}
          onClick={() => { setMode('search'); setSelectedWineId(null); }}
        >
          {t('grapeEscape.addWine.searchExisting', 'Search Existing')}
        </button>
        <button
          className={`ge-toggle-btn ${mode === 'new' ? 'active' : ''}`}
          onClick={() => { setMode('new'); setSelectedWineId(null); }}
        >
          {t('grapeEscape.addWine.createNew', 'Create New')}
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Search existing wine */}
        {mode === 'search' && (
          <div className="ge-wine-search ge-card">
            <div className="ge-search-row">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={t('grapeEscape.addWine.searchPlaceholder', 'Search wines...')}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleSearch())}
                aria-label={t('grapeEscape.addWine.searchPlaceholder', 'Search wines...')}
              />
              <button type="button" className="ge-btn-secondary" onClick={handleSearch}>
                {t('grapeEscape.addWine.search', 'Search')}
              </button>
            </div>
            {searchResults.length > 0 && (
              <div className="ge-search-results">
                {searchResults.map(wine => (
                  <div
                    key={wine.id}
                    className={`ge-search-result ${selectedWineId === wine.id ? 'selected' : ''}`}
                    onClick={() => setSelectedWineId(wine.id)}
                  >
                    <span className="ge-search-name">{wine.name}</span>
                    <span className="ge-search-meta">
                      {wine.producer} {wine.vintage && `· ${wine.vintage}`} {wine.type && `· ${wine.type}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Create new wine */}
        {mode === 'new' && (
          <div className="ge-card" style={{ marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 12px', color: 'var(--text-primary)' }}>
              {t('grapeEscape.addWine.wineDetails', 'Wine Details')}
            </h4>
            <div className="ge-form-grid">
              <div className="ge-form-group">
                <label>{t('grapeEscape.addWine.name', 'Name')} *</label>
                <input type="text" value={wineName} onChange={e => setWineName(e.target.value)} required aria-required="true" />
              </div>
              <div className="ge-form-group">
                <label>{t('grapeEscape.addWine.producer', 'Producer')}</label>
                <input
                  type="text"
                  list="producers-list"
                  value={producer}
                  onChange={e => setProducer(e.target.value)}
                />
                <datalist id="producers-list">
                  {producers.map((p, i) => <option key={i} value={typeof p === 'string' ? p : p.name} />)}
                </datalist>
              </div>
              <div className="ge-form-group">
                <label>{t('grapeEscape.addWine.region', 'Region')}</label>
                <input
                  type="text"
                  list="regions-list"
                  value={region}
                  onChange={e => setRegion(e.target.value)}
                />
                <datalist id="regions-list">
                  {regions.map((r, i) => <option key={i} value={typeof r === 'string' ? r : r.name} />)}
                </datalist>
              </div>
              <div className="ge-form-group">
                <label>{t('grapeEscape.addWine.vintage', 'Vintage')}</label>
                <input type="number" value={vintage} onChange={e => setVintage(e.target.value)} min="1900" max="2099" />
              </div>
              <div className="ge-form-group">
                <label>{t('grapeEscape.addWine.type', 'Type')}</label>
                <select value={wineType} onChange={e => setWineType(e.target.value)}>
                  {WINE_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                </select>
              </div>
              <div className="ge-form-group">
                <label>{t('grapeEscape.addWine.grapeBlend', 'Grape Blend')}</label>
                <input type="text" value={grapeBlend} onChange={e => setGrapeBlend(e.target.value)} placeholder="e.g. Cabernet Sauvignon, Merlot" />
              </div>
              <div className="ge-form-group">
                <label>{t('grapeEscape.addWine.alcohol', 'Alcohol %')}</label>
                <input type="number" step="0.1" value={alcoholPct} onChange={e => setAlcoholPct(e.target.value)} min="0" max="100" />
              </div>
            </div>
          </div>
        )}

        {/* Cellar Entry Fields */}
        <div className="ge-card">
          <h4 style={{ margin: '0 0 12px', color: 'var(--text-primary)' }}>
            {t('grapeEscape.addWine.cellarDetails', 'Cellar Entry Details')}
          </h4>
          <div className="ge-form-grid">
            <div className="ge-form-group">
              <label>{t('grapeEscape.addWine.quantity', 'Quantity')}</label>
              <input type="number" value={quantity} onChange={e => setQuantity(e.target.value)} min="1" />
            </div>
            <div className="ge-form-group">
              <label>{t('grapeEscape.addWine.purchasePrice', 'Purchase Price')}</label>
              <input type="number" step="0.01" value={purchasePrice} onChange={e => setPurchasePrice(e.target.value)} />
            </div>
            <div className="ge-form-group">
              <label>{t('grapeEscape.addWine.currency', 'Currency')}</label>
              <select value={currency} onChange={e => setCurrency(e.target.value)}>
                <option value="NOK">NOK</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
            <div className="ge-form-group">
              <label>{t('grapeEscape.addWine.purchaseDate', 'Purchase Date')}</label>
              <input type="date" value={purchaseDate} onChange={e => setPurchaseDate(e.target.value)} />
            </div>
            <div className="ge-form-group">
              <label>{t('grapeEscape.addWine.storageLocation', 'Storage Location')}</label>
              <input type="text" value={storageLocation} onChange={e => setStorageLocation(e.target.value)} />
            </div>
            <div className="ge-form-group">
              <label>{t('grapeEscape.addWine.drinkFrom', 'Drink From')}</label>
              <input type="date" value={drinkFrom} onChange={e => setDrinkFrom(e.target.value)} />
            </div>
            <div className="ge-form-group">
              <label>{t('grapeEscape.addWine.drinkBy', 'Drink By')}</label>
              <input type="date" value={drinkBy} onChange={e => setDrinkBy(e.target.value)} />
            </div>
            <div className="ge-form-group full-width">
              <label>{t('grapeEscape.addWine.notes', 'Notes')}</label>
              <textarea rows="2" value={notes} onChange={e => setNotes(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="ge-form-actions">
          <button type="submit" className="ge-btn-primary" disabled={saving}>
            {saving
              ? t('grapeEscape.addWine.saving', 'Saving...')
              : t('grapeEscape.addWine.save', 'Add to Cellar')}
          </button>
          <button type="button" className="ge-btn-secondary" onClick={onCancel}>
            {t('grapeEscape.addWine.cancel', 'Cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddWineForm;
